import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import { instancesAPI } from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const VERTICALS = [
  { value: "", label: "Selecione..." },
  { value: "AUTOMOTIVE", label: "Automotivo" },
  { value: "BEAUTY", label: "Beleza" },
  { value: "CLOTHING", label: "Vestuário" },
  { value: "EDU", label: "Educação" },
  { value: "ENTERTAIN", label: "Entretenimento" },
  { value: "EVENT_PLAN", label: "Eventos" },
  { value: "FINANCE", label: "Finanças" },
  { value: "GROCERY", label: "Alimentação" },
  { value: "HEALTH", label: "Saúde" },
  { value: "HOTEL", label: "Hotelaria" },
  { value: "NONPROFIT", label: "ONG" },
  { value: "PROF_SERVICES", label: "Serviços Profissionais" },
  { value: "RESTAURANT", label: "Restaurante" },
  { value: "RETAIL", label: "Varejo" },
  { value: "TRAVEL", label: "Viagem" },
  { value: "OTHER", label: "Outro" },
];

export default function WhatsAppProfileModal({ instanceId, onClose }) {
  const { darkMode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    about: "",
    description: "",
    address: "",
    email: "",
    websites: ["", ""],
    vertical: "",
    display_name: "",
  });

  useEffect(() => {
    instancesAPI.getBusinessProfile(instanceId)
      .then((r) => {
        const p = r.data;
        setForm({
          about: p.about || "",
          description: p.description || "",
          address: p.address || "",
          email: p.email || "",
          websites: [p.websites?.[0] || "", p.websites?.[1] || ""],
          vertical: p.vertical || "",
          display_name: p.display_name || "",
        });
      })
      .catch((e) => toast.error("Erro ao carregar perfil: " + (e.response?.data?.message || e.message)))
      .finally(() => setLoading(false));
  }, [instanceId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Payload limpo (só campos da API)
      const { _photoFile, _photoPreview, display_name, ...cleanPayload } = form;
      const payload = { ...cleanPayload };
      payload.websites = payload.websites.filter((w) => w.trim());

      // Atualizar display name separadamente (requer aprovação da Meta)
      if (display_name && display_name !== form._originalDisplayName) {
        try {
          await instancesAPI.updateDisplayName(instanceId, display_name);
          toast.success("Nome de exibição enviado pra aprovação da Meta");
        } catch (e) {
          toast.error("Erro ao alterar nome: " + (e.response?.data?.message || e.message));
        }
      }

      // Upload foto se selecionada
      if (_photoFile) {
        const formData = new FormData();
        formData.append('file', _photoFile);
        try {
          const uploadRes = await instancesAPI.uploadProfilePhoto(instanceId, formData);
          if (uploadRes.data?.handle) {
            payload.profile_picture_handle = uploadRes.data.handle;
          }
        } catch (e) {
          toast.error("Erro no upload da foto: " + (e.response?.data?.message || e.message));
          setSaving(false);
          return; // Não salva o resto se o upload falhou
        }
      }

      await instancesAPI.updateBusinessProfile(instanceId, payload);
      toast.success("Perfil atualizado com sucesso!");
      onClose();
    } catch (e) {
      toast.error("Erro: " + (e.response?.data?.message || e.message));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = `w-full px-3 py-2 rounded-md border text-sm ${
    darkMode ? "bg-zinc-700 border-zinc-600 text-white" : "bg-white border-gray-300 text-gray-900"
  }`;
  const labelCls = `block text-sm font-medium mb-1 ${darkMode ? "text-gray-200" : "text-gray-700"}`;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className={`${darkMode ? "bg-zinc-800 text-white" : "bg-white text-gray-900"} rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex items-center justify-between px-6 py-4 border-b ${darkMode ? "border-zinc-700" : "border-gray-200"}`}>
          <h2 className="text-lg font-bold">Editar Perfil WhatsApp</h2>
          <button onClick={onClose} className="hover:opacity-70"><X size={18} /></button>
        </div>

        {loading ? (
          <div className="p-8 text-center">Carregando...</div>
        ) : (
          <div className="px-6 py-4 space-y-4">
            {/* Foto de perfil */}
            <div>
              <label className={labelCls}>Foto de perfil</label>
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 5 * 1024 * 1024) {
                      toast.error("Imagem deve ter no máximo 5MB");
                      return;
                    }
                    setForm({ ...form, _photoFile: file, _photoPreview: URL.createObjectURL(file) });
                  }}
                  className={`text-sm ${darkMode ? "text-gray-300" : "text-gray-700"}`}
                />
                {form._photoPreview && (
                  <img src={form._photoPreview} alt="Preview" className="w-12 h-12 rounded-full object-cover" />
                )}
              </div>
              <span className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>JPG ou PNG, máx 5MB, mínimo 192x192px</span>
            </div>

            <div>
              <label className={labelCls}>Nome de exibição <span className="text-xs opacity-60">(requer aprovação da Meta)</span></label>
              <input
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                placeholder="Nome que aparece no WhatsApp"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Sobre (status)</label>
              <input
                value={form.about}
                onChange={(e) => setForm({ ...form, about: e.target.value })}
                maxLength={139}
                placeholder="Ex: Atendimento de seg a sex, 9h-18h"
                className={inputCls}
              />
              <span className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>{form.about.length}/139</span>
            </div>

            <div>
              <label className={labelCls}>Descrição</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                maxLength={512}
                rows={3}
                placeholder="Descreva seu negócio..."
                className={inputCls}
              />
            </div>

            <div>
              <label className={labelCls}>Endereço</label>
              <input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                maxLength={256}
                placeholder="Rua, número, cidade - UF"
                className={inputCls}
              />
            </div>

            <div>
              <label className={labelCls}>Email</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                maxLength={128}
                placeholder="contato@empresa.com.br"
                className={inputCls}
              />
            </div>

            <div>
              <label className={labelCls}>Website 1</label>
              <input
                value={form.websites[0]}
                onChange={(e) => setForm({ ...form, websites: [e.target.value, form.websites[1]] })}
                placeholder="https://www.empresa.com.br"
                className={inputCls}
              />
            </div>

            <div>
              <label className={labelCls}>Website 2 (opcional)</label>
              <input
                value={form.websites[1]}
                onChange={(e) => setForm({ ...form, websites: [form.websites[0], e.target.value] })}
                placeholder="https://loja.empresa.com.br"
                className={inputCls}
              />
            </div>

            <div>
              <label className={labelCls}>Categoria</label>
              <select
                value={form.vertical}
                onChange={(e) => setForm({ ...form, vertical: e.target.value })}
                className={inputCls}
              >
                {VERTICALS.map((v) => (
                  <option key={v.value} value={v.value}>{v.label}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className={`flex justify-end gap-2 px-6 py-3 border-t ${darkMode ? "border-zinc-700 bg-zinc-900" : "border-gray-200 bg-gray-50"}`}>
          <button onClick={onClose} className={`px-4 py-2 rounded-md text-sm ${darkMode ? "bg-zinc-700 text-gray-200" : "bg-gray-100 text-gray-700"}`}>
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="px-4 py-2 rounded-md text-sm bg-nomoz-orange text-white hover:opacity-90 disabled:opacity-50"
          >
            {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}
